import { useI18n } from '@/i18n';
import type { CouponTerms } from '@/lib/stripeDiscounts';
import type { Locale } from 'date-fns';
import { differenceInHours, format, formatDistance } from 'date-fns';
import { da, de, enUS, es, fi, fr, it, nb, nl, pl, pt, sv } from 'date-fns/locale';
import type { Stripe } from 'stripe';
import { computed } from 'vue';

interface StripeInvoice {
  id: string;
  created: number;
  amount_due: number;
  currency: string;
}

interface StripeDiscount {
  end: number | null;
}

interface StripeSubscription {
  id: string;
  plan: { id: string; product: { id: string } };
  status: string;
}

interface Offering {
  products: any;
}

const dateFnsLocales: Record<string, Locale> = { da, de, en: enUS, es, fi, fr, it, nb, nl, pl, pt, sv };

export function dateFnsLocaleForI18n(code: string): Locale {
  return dateFnsLocales[code.split('-')[0]] ?? enUS;
}

/** Dollars that aren't US dollars: shown with their full symbol (A$, NZ$, CA$), never a bare "$". */
const otherDollars = new Set(['AUD', 'NZD', 'CAD']);

const zeroDecimalCurrencies = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

export interface TaxSplit {
  /** Tax added on top of the price (US/CA); subtotal + exclusive = total. */
  exclusive: number;
  /** Tax already contained in the price (EU VAT etc.); does not add to the total. */
  inclusive: number;
}

/** Just the fields the trial-end computation reads — kept minimal so callers can pass a
 *  full Stripe.Invoice/Subscription without tripping TS's recursive-type depth guard. */
interface TrialEndInput {
  subscription: { status?: string; trial_end?: number | null } | null | undefined;
  upcomingInvoice:
    | { amount_due?: number | null; lines?: { data?: Array<{ period?: { end?: number | null } }> } }
    | null
    | undefined;
  recurringInvoice: { lines?: { data?: Array<{ period?: { start?: number | null } }> } } | null | undefined;
  planAmount: number;
}

/**
 * Seconds-epoch of the first real charge, but ONLY when it genuinely lands later than
 * today — a new subscriber whose first period is fully discounted (e.g. a 100% coupon).
 * Returns null otherwise, including for a live trial, which checkout ends immediately.
 *
 * An ordinary plan change whose proration credit zeroes the invoice is NOT a trial:
 * Stripe clamps the negative invoice to `amount_due: 0`, and the rest layer may still
 * hand back a recurring preview. Keying a "trial" off either signal mislabelled a
 * monthly→yearly upgrade as a trial and rendered a bogus "won't be charged until"
 * date (DAB-754). So the trial context is established first; only then is a
 * recurring/period-derived date trusted.
 */
export function computeTrialEndSeconds({
  subscription,
  upcomingInvoice,
  recurringInvoice,
  planAmount,
}: TrialEndInput): number | null {
  // Checking out ends a live trial on the spot and charges today, so there is no
  // later first-charge date to promise. Only a subscriber-to-be whose first period
  // is fully discounted still gets one.
  if (subscription?.status === 'trialing') return null;

  const isTrial = !subscription && upcomingInvoice?.amount_due === 0 && planAmount > 0;
  if (!isTrial) return null;

  const recurringStart = recurringInvoice?.lines?.data?.[0]?.period?.start;
  if (recurringStart) return recurringStart;
  return upcomingInvoice?.lines?.data?.[0]?.period?.end ?? null;
}

/** Split Stripe invoice taxes by behavior so exclusive tax can be a line and inclusive tax a note. */
export function splitTax(taxes: Stripe.Invoice.TotalTax[] | null | undefined): TaxSplit {
  let exclusive = 0;
  let inclusive = 0;
  for (const tax of taxes ?? []) {
    if (tax.tax_behavior === 'inclusive') inclusive += tax.amount || 0;
    else exclusive += tax.amount || 0;
  }
  return { exclusive, inclusive };
}

export function useBillingUtils() {
  const { t, locale } = useI18n();
  const dateOptions = computed(() => ({ locale: dateFnsLocaleForI18n(locale.value) }));

  /** Locale-aware date for a Unix-seconds timestamp ('' when absent). */
  function formatUnixDate(ts: number | null | undefined, pattern = 'PP'): string {
    return ts != null ? format(new Date(ts * 1000), pattern, dateOptions.value) : '';
  }

  function formatPrice(total: number, currency: string = 'USD', dropZeros?: boolean): string {
    let minimumFractionDigits = 0;
    const code = currency.toUpperCase();
    if (!zeroDecimalCurrencies.has(code)) total /= 100;
    if (!dropZeros || total % 1 !== 0) minimumFractionDigits = 2;
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency,
      // The narrow symbol prints A$, NZ$ and CA$ as a bare "$", which reads as US dollars — an AUD
      // subscriber took A$237.60 beside a USD "$276" for wrong discount maths (DAB-1996).
      currencyDisplay: otherDollars.has(code) ? 'symbol' : 'narrowSymbol',
      minimumFractionDigits,
    }).format(total);
  }

  function paymentString(invoice: StripeInvoice): string {
    const currency = invoice.currency.toUpperCase();
    const opts = {
      total: formatPrice(invoice.amount_due, currency) + ' ' + currency,
      date: format(new Date(invoice.created * 1000), 'PPP', dateOptions.value),
    };
    return t('billing_payment', opts);
  }

  /** Upcoming invoice preview: not a completed payment — use due date, not "Paid …". */
  function upcomingInvoicePaymentString(invoice: Stripe.Invoice): string {
    const currency = invoice.currency.toUpperCase();
    const total = formatPrice(invoice.amount_due, currency, true) + ' ' + currency;
    const ts =
      invoice.next_payment_attempt ?? invoice.period_end ?? invoice.lines?.data?.[0]?.period?.end ?? invoice.created;
    const date = format(new Date(ts * 1000), 'PPP', dateOptions.value);
    return t('billing_upcoming_payment', { total, date });
  }

  /** Percents and months can be fractional: 12.5% must not read as 13%. */
  function formatDecimal(n: number): string {
    return new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(n);
  }

  function couponAmount(coupon: CouponTerms): string {
    return coupon.amount_off
      ? formatPrice(coupon.amount_off, coupon.currency || undefined)
      : `${formatDecimal(coupon.percent_off ?? 0)}%`;
  }

  /** What a coupon takes off, "50%" or "$5", or null when it takes nothing. */
  function couponValue(coupon: CouponTerms): string | null {
    if (coupon.percent_off != null && coupon.percent_off > 0) {
      return t('billing_coupon_ticket_percent', { percent: formatDecimal(coupon.percent_off) });
    }
    if (coupon.amount_off != null && coupon.amount_off > 0) {
      return formatPrice(coupon.amount_off, coupon.currency || undefined, true);
    }
    return null;
  }

  /** The term printed on a coupon ticket beside its value: "for 3 months", "forever". */
  function couponTicketTerm(coupon: CouponTerms): string {
    const months = coupon.duration_in_months ?? 0;
    if (coupon.duration === 'forever') return t('billing_coupon_ticket_forever');
    if (coupon.duration === 'once') return t('billing_coupon_ticket_once');
    if (coupon.duration !== 'repeating') return '';
    return months === 1 ? t('billing_coupon_ticket_one_month') : t('billing_coupon_ticket_months', { months });
  }

  /**
   * Natural sentence for the active-coupon alert (no bullet separators; avoids the word “Repeating”).
   */
  function couponDiscountBenefit(coupon: CouponTerms): string {
    const pct = coupon.percent_off != null && coupon.percent_off > 0 ? formatDecimal(coupon.percent_off) : null;
    const amt =
      coupon.amount_off != null && coupon.amount_off > 0
        ? formatPrice(coupon.amount_off, coupon.currency || undefined, true)
        : null;

    if (pct != null) {
      if (coupon.duration === 'repeating') {
        const months = coupon.duration_in_months ?? 0;
        if (months === 1) {
          return t('billing_discount_percent_repeating_one_month', { percent: pct });
        }
        return t('billing_discount_percent_repeating', { percent: pct, months: formatDecimal(months) });
      }
      if (coupon.duration === 'once') {
        return t('billing_discount_percent_once', { percent: pct });
      }
      if (coupon.duration === 'forever') {
        return t('billing_discount_percent_forever', { percent: pct });
      }
    }

    if (amt != null) {
      if (coupon.duration === 'once') {
        return t('billing_discount_amount_once', { amount: amt });
      }
      if (coupon.duration === 'repeating') {
        const months = coupon.duration_in_months ?? 0;
        if (months === 1) {
          return t('billing_discount_amount_repeating_one_month', { amount: amt });
        }
        return t('billing_discount_amount_repeating', { amount: amt, months: formatDecimal(months) });
      }
      if (coupon.duration === 'forever') {
        return t('billing_discount_amount_forever', { amount: amt });
      }
    }

    return '';
  }

  /** "Spring Sale (20% off for 3 months)": never a Stripe id, and the terms alone without a name. */
  function couponNameAndTerms(name: string, coupon: CouponTerms): string {
    const terms = couponDiscountBenefit(coupon);
    return name && terms ? `${name} (${terms})` : name || terms;
  }

  function discountEnd(discount: StripeDiscount): string {
    return (discount.end && formatDistance(new Date(discount.end * 1000), new Date(), dateOptions.value)) || '';
  }

  function getCurrentPlan(subs: StripeSubscription[], planIds: string[]) {
    if (!planIds || !subs || subs.length === 0) return null;

    for (const sub of subs) {
      const productId = sub.plan?.product?.id;
      if (productId && planIds.includes(productId)) {
        return sub.plan;
      }
    }
    const firstActive = subs.find((sub) => sub.status === 'active' || sub.status === 'trialing');
    return firstActive ? firstActive.plan : null;
  }

  function getPlanIds(offerings: Offering | null): string[] {
    if (!offerings?.products) return [];

    const ids: string[] = [];
    const plans = offerings.products;

    if (Array.isArray(plans)) {
      plans.forEach((product) => {
        Object.values(product).forEach((interval: any) => {
          if (typeof interval !== 'string') {
            Object.values(interval).forEach((currencyPlan: any) => {
              if (typeof currencyPlan === 'string') {
                ids.push(currencyPlan);
              }
            });
          }
        });
      });
    } else if (typeof plans === 'object') {
      Object.values(plans).forEach((product: any) => {
        Object.values(product).forEach((interval: any) => {
          if (interval && typeof interval !== 'string') {
            Object.values(interval).forEach((currencyPlan: any) => {
              if (typeof currencyPlan === 'string') {
                ids.push(currencyPlan);
              }
            });
          }
        });
      });
    }

    return [...new Set(ids)];
  }

  /**
   * Confirm-dialog body for cancelling a TRIAL. Cancelling a trial takes effect immediately —
   * the server cancels outright rather than at period end, so the cancellation cannot be
   * silently reversed by the customer.updated webhook (DAB-922 / DAB-961). The user loses the
   * rest of their trial, so say so before they commit.
   *
   * When the trial still has room, also offer the no-loss alternative: removing the payment
   * method lets the trial run its course and lapse unbilled. That only works while
   * `customer.subscription.trial_will_end` (which Stripe fires 3 days out) has yet to run —
   * it is what sets cancel_at_period_end once it sees no card. Remove the card after it has
   * already fired and the trial instead ends in a failed invoice and a delinquent, read-only
   * account. Hence the floor of 96 hours: 72 for the webhook, 24 of margin.
   *
   * Measured in HOURS, not calendar days. differenceInCalendarDays counts local midnight
   * boundaries, so "4 days" spanning 23:59 → 00:01 is barely 3 real days and the margin the
   * comment claims would not exist.
   */
  function trialCancelConfirmBody(trialEndSeconds?: number | null): string {
    if (!trialEndSeconds) return t('billing_cancel_confirm_trial_body_unknown');

    const trialEnd = new Date(trialEndSeconds * 1000);
    const hoursLeft = differenceInHours(trialEnd, new Date());
    if (hoursLeft <= 0) return t('billing_cancel_confirm_trial_body_unknown');

    // Deliberately phrased around the end date rather than a day count: a count would need
    // plural forms in all 12 locales (Polish alone has four) to read correctly at "1 day".
    const body = t('billing_cancel_confirm_trial_body', { date: format(trialEnd, 'PPP', dateOptions.value) });

    // The markup is here because of quill-ui's `Confirm`, not because of anything in this
    // function: it binds the body via innerHTML and ships no white-space rule, so a newline
    // collapses and the two sentences run together. If Confirm ever gains `white-space: pre-line`
    // or moves the body to a slot, drop the <br> and use \n\n. Safe today because every part is a
    // static translation or a formatted date — never interpolate user-supplied text into this
    // string, since innerHTML makes it an injection sink.
    return hoursLeft < 96 ? body : `${body}<br><br>${t('billing_cancel_trial_keep_time_hint')}`;
  }

  /**
   * Confirmation body for cancelling a *paused* subscription. Mirrors dabble-rest's rule exactly
   * (billing.ts cancelSubscription): a pause with prepaid time left cancels at period end, and
   * only a pause whose period has already run out cancels outright. The server treats a missing
   * period end as "time remains" (the safe direction), so the client says the same.
   *
   * Takes the item-level `current_period_end` — the field the server reads, and where the
   * period lives in the API version this codebase targets.
   */
  function pausedCancelConfirmBody(periodEndSeconds?: number | null): string {
    if (typeof periodEndSeconds !== 'number') return t('billing_cancel_confirm_paused_period_body_unknown');

    const periodEnd = new Date(periodEndSeconds * 1000);
    if (periodEnd.getTime() <= Date.now()) return t('billing_cancel_confirm_paused_body');

    return t('billing_cancel_confirm_paused_period_body', { date: format(periodEnd, 'PPP', dateOptions.value) });
  }

  return {
    formatUnixDate,
    formatPrice,
    paymentString,
    trialCancelConfirmBody,
    pausedCancelConfirmBody,
    upcomingInvoicePaymentString,
    formatDecimal,
    couponAmount,
    couponValue,
    couponDiscountBenefit,
    couponNameAndTerms,
    couponTicketTerm,
    discountEnd,
    getCurrentPlan,
    getPlanIds,
  };
}
